'use client'

import { toast } from '@/core/lib/toast'

import AdultVerifyModal from '@/features/veluvit/components/account/adult-verify-modal'
import {
  ADULT_REQUIRED,
  refreshAdult,
} from '@/features/veluvit/services/adult.service'
import { api, ApiError } from '@/features/veluvit/services/api'
import { chatModelSchema } from '@/features/veluvit/schemas/api.schema'
import { useChatStore } from '@/features/veluvit/stores/chat.store'
import { closeLayer, openModal } from '@/features/veluvit/stores/overlay.store'
import {
  roomFor,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'
import { refreshSession } from '@/features/veluvit/services/session.service'

import type {
  ChatModel,
  PresetId,
  Room,
} from '@/features/veluvit/types/api.types'

/* 답장 스타일(스토리형·대화형·성인모드) — 같은 방에서 PATCH 로 바꾼다. 성인모드는 서버의 성인 인증 판정이 있어야 한다.
   잠금 해제 근거는 언제나 서버 값(config.adult.eligible)이다 — 화면이 기억한 상태나 본인확인 성공 여부로 열지 않는다. */

// 성인모드의 연출(direct) 모드 막기는 2026-10-13 걷었다 — 서버(fd83454)는 성인 인증만 보고 모드를 막지 않고,
// 연출 모드도 이제 이미지를 새로 만들지 않는다(등록 이미지에서 고른다). 서버가 막으면 그 거절 문구가 그대로 보인다
const changingRooms = new Set<string>()
const applyingRooms = new Map<string, Promise<boolean>>()

function queuedSettings(workId: string, roomId: string) {
  const queued = useChatStore.getState().get(workId).nextSettings
  return queued?.roomId === roomId ? queued : null
}

function queueSetting(
  workId: string,
  roomId: string,
  setting: { model: string } | { preset: PresetId }
) {
  useChatStore.getState().patch(workId, {
    nextSettings: { ...queuedSettings(workId, roomId), roomId, ...setting },
  })
}

/** 응답 중에 고른 모델·답장 스타일을 방에 반영한다. 반영했으면 true */
export function applyNextSettings(workId: string): Promise<boolean> {
  const store = useChatStore.getState()
  const queued = store.get(workId).nextSettings
  if (!queued) return Promise.resolve(false)
  const room = roomFor(workId)
  if (room?.id !== queued.roomId) {
    store.patch(workId, { nextSettings: null })
    return Promise.resolve(false)
  }
  const running = applyingRooms.get(room.id)
  if (running) return running
  const { roomId, ...body } = queued
  const identity = useSessionStore.getState().identity
  const current = () =>
    identity === useSessionStore.getState().identity &&
    roomFor(workId)?.id === roomId
  changingRooms.add(roomId)
  const request = api<Room>(`/room/${roomId}/`, body, 'PATCH')
    .then((next) => {
      if (!current()) return false
      useSessionStore.getState().applyRoom(next)
      return true
    })
    .catch((error: unknown) => {
      if (!current()) return false
      if (error instanceof ApiError && error.code === ADULT_REQUIRED)
        void refreshAdult().catch(() => null)
      toast.show(
        error instanceof Error
          ? error.message
          : '설정을 바꾸지 못했어요. 다시 골라 주세요.'
      )
      return false
    })
    .finally(() => {
      changingRooms.delete(roomId)
      applyingRooms.delete(roomId)
      if (useChatStore.getState().get(workId).nextSettings === queued)
        useChatStore.getState().patch(workId, { nextSettings: null })
    })
  applyingRooms.set(roomId, request)
  return request
}

/** 성인 인증 창을 연다. 인증을 마치고 서버가 이용 가능하다고 답하면 onEligible 을 부른다 */
export function openAdultVerify(onEligible?: () => void) {
  let layerId: string | null = null
  const close = () => {
    if (layerId) closeLayer(layerId)
  }
  layerId = openModal(
    <AdultVerifyModal onEligible={onEligible} onClose={close} />,
    { label: '성인 인증', key: 'adult-verification' }
  )
}

/** 방의 답장 스타일을 바꾼다. 성인모드인데 인증이 없으면 인증 창부터 띄우고, 통과하면 이어서 바꾼다 */
export async function changePreset(
  workId: string,
  preset: PresetId
): Promise<void> {
  const room = roomFor(workId)
  if (!room) return
  const busy = useChatStore.getState().get(workId).busy
  if (!busy && changingRooms.has(room.id)) {
    toast.show('설정을 바꾸는 중이에요. 잠시 후 다시 골라 주세요.')
    return
  }
  const queued = queuedSettings(workId, room.id)
  if ((queued?.preset || room.preset || 'story') === preset) return
  const config = useSessionStore.getState().data?.config
  const target = config?.presets?.find((p) => p.id === preset)
  if (target?.requiresAdult && !config?.adult?.eligible) {
    openAdultVerify(() => void changePreset(workId, preset))
    return
  }
  if (busy || queued) {
    queueSetting(workId, room.id, { preset })
    toast.show(
      `${target?.name || '답장 스타일'}로 바꿨어요 · 다음 답장부터 적용돼요`,
      { duration: 2200 }
    )
    return
  }
  const identity = useSessionStore.getState().identity
  changingRooms.add(room.id)
  try {
    const next = await api<Room>(`/room/${room.id}/`, { preset }, 'PATCH')
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    useSessionStore.getState().applyRoom(next)
    toast.show(
      `${target?.name || '답장 스타일'}로 바꿨어요 · 다음 답장부터 적용돼요`,
      { duration: 2200 }
    )
  } catch (error) {
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    if (error instanceof ApiError && error.code === ADULT_REQUIRED) {
      // 서버가 인증 만료·미성년 등으로 거절했다 — 기억한 상태를 버리고 서버 상태로 다시 안내한다
      await refreshAdult().catch(() => null)
      openAdultVerify(() => void changePreset(workId, preset))
      return
    }
    toast.show(error instanceof Error ? error.message : '다시 시도해 주세요.')
  } finally {
    changingRooms.delete(room.id)
  }
}

/** 모델 목록이 초기 데이터에 없으면(예전 서버) 따로 받아 세션에 넣는다 */
export async function loadModels() {
  const initial = useSessionStore.getState().data
  if (!initial || initial.config.models) return
  if (initial.user) {
    const refreshed = await refreshSession()
    if (!refreshed.config.models)
      throw new Error(
        '계정의 모델 설정을 확인하지 못했어요. 잠시 후 다시 불러와 주세요.'
      )
    return
  }
  const data = await api<{ models: unknown[]; default: string }>('/model/')
  const session = useSessionStore.getState().data
  // 공개 목록에는 계정별 reasoning 권한이 없다. 인증된 bootstrap 설정을 덮지 않는다.
  if (!session || session.user || session.config.models) return
  const models = chatModelSchema.array().parse(data.models) as ChatModel[]
  useSessionStore.setState({
    data: {
      ...session,
      config: { ...session.config, models, model: data.default },
    },
  })
}

/** 방의 대화 모델을 바꾼다 — 다음 답장부터 적용. 응답 중에는 서버가 409 로 막으므로 골라 두고 끝난 뒤 반영한다 */
export async function changeModel(
  workId: string,
  model: string
): Promise<void> {
  const room = roomFor(workId)
  if (!room) return
  const busy = useChatStore.getState().get(workId).busy
  if (!busy && changingRooms.has(room.id)) {
    toast.show('설정을 바꾸는 중이에요. 잠시 후 다시 골라 주세요.')
    return
  }
  const queued = queuedSettings(workId, room.id)
  const config = useSessionStore.getState().data?.config
  if ((queued?.model || room.model || config?.model) === model) return
  const target = config?.models?.find((m) => m.id === model)
  if (!target?.enabled) {
    toast.show(
      target?.unavailableMessage ||
        '지금 선택할 수 없는 모델이에요. 모델 목록을 다시 확인해 주세요.'
    )
    return
  }
  if (busy || queued) {
    queueSetting(workId, room.id, { model })
    toast.show(`${target.name || '모델'}로 바꿨어요 · 다음 답장부터 적용돼요`, {
      duration: 2200,
    })
    return
  }
  const identity = useSessionStore.getState().identity
  changingRooms.add(room.id)
  try {
    const next = await api<Room>(`/room/${room.id}/`, { model }, 'PATCH')
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    useSessionStore.getState().applyRoom(next)
    toast.show(
      `${target?.name || '모델'}로 바꿨어요 · 다음 답장부터 적용돼요`,
      { duration: 2200 }
    )
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(error instanceof Error ? error.message : '다시 시도해 주세요.')
  } finally {
    changingRooms.delete(room.id)
  }
}
